import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Loader, AlertTriangle, ArrowRight, Sparkles, ShieldCheck } from "lucide-react";
import { api } from "../lib/api";
import ResultSection from "../components/ResultSection";
import { RK, ReportList } from "../components/reports/ReportKit";

const GROUP = "public-strategy-brief";
const ORANGE = "#FF6A00";
const NAVY = "#0F1B2D";

/**
 * PublicStrategyBrief — read-only public view of a Strategy Brief, accessed
 * via /strategy-brief/share/:token after the owner emails it (or copies the
 * link). Anonymous — no login required, no edits, no PII beyond what's in
 * the brief itself (which is JD-derived, not personal).
 *
 * Backend: GET /api/public/strategy-brief/{token}
 */
export default function PublicStrategyBrief() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await api.get(`/public/strategy-brief/${token}`);
        if (alive) setData(res.data);
      } catch (e) {
        if (alive) setError(e.response?.data?.detail || "This link is invalid or sharing was revoked.");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [token]);

  if (loading) {
    return (
      <div className="min-h-screen rm-paper flex items-center justify-center">
        <Loader size={28} className="animate-spin rm-text-orange" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="min-h-screen rm-paper flex items-center justify-center p-6">
        <div className="rm-card-static p-6 max-w-md text-center"
             style={{ background: "#FEE2E2", borderColor: "#B91C1C" }}
             data-testid="public-brief-error">
          <AlertTriangle size={32} style={{ color: "#B91C1C", margin: "0 auto" }} />
          <h2 className="font-display uppercase text-xl mt-3"
              style={{ color: NAVY, letterSpacing: "-0.02em" }}>Link unavailable</h2>
          <p className="text-sm mt-1" style={{ color: "#991B1B" }}>{error || "Try requesting a fresh link from the sharer."}</p>
          <Link to="/" className="rm-btn rm-btn-primary mt-4 text-sm px-3 py-2 inline-flex">
            Go to Resmo <ArrowRight size={12} />
          </Link>
        </div>
      </div>
    );
  }

  const b = data.brief || {};

  return (
    <div className="min-h-screen rm-paper" data-testid="public-strategy-brief">
      <div className="max-w-3xl mx-auto px-4 md:px-8 py-8 md:py-10">
        <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
          <Link to="/" className="font-display uppercase text-2xl"
                style={{ color: NAVY, letterSpacing: "-0.02em" }}>
            Resmo
          </Link>
          <span className="font-mono-rm uppercase text-[10px] tracking-widest inline-flex items-center gap-1 px-2 py-1 rounded"
                style={{ background: "#D1FAE5", color: "#064E3B", border: "1px solid #047857", fontWeight: 700 }}
                data-testid="public-brief-readonly-pill">
            <ShieldCheck size={11} /> Read-only · shared by {data.shared_by}
          </span>
        </div>

        <div className="rm-card-static p-5 md:p-6"
             style={{ background: `linear-gradient(135deg, #FBF7EE 0%, #FFE4D2 100%)`, borderColor: NAVY, boxShadow: `4px 4px 0 0 ${ORANGE}` }}
             data-testid="public-brief-hero">
          <div className="font-mono-rm uppercase text-[10px] tracking-widest mb-1"
               style={{ color: ORANGE, fontWeight: 700 }}>
            · Resmo Strategy Brief
          </div>
          <h1 className="font-display uppercase leading-tight"
              style={{ fontSize: "clamp(1.4rem, 3vw, 2.2rem)", color: NAVY, letterSpacing: "-0.02em" }}>
            {data.job_title || "Tailored brief"}{data.company_name ? <span style={{ color: "#475569" }}>  ·  {data.company_name}</span> : null}
          </h1>
          <p className="text-sm mt-1" style={{ color: "#475569" }}>
            A consolidated coach's briefing — the resume read, the JD priorities, the network plays, and the next steps to win this interview.
          </p>
        </div>

        <div className="space-y-3 mt-4" data-testid="public-brief-sections">
          {b.problem_statement && (
            <ResultSection title="The real problem this role solves" eyebrow="Section 1" accentGradient="linear-gradient(135deg,#F97316 0%,#B91C1C 100%)" defaultOpen group={GROUP} testid="public-brief-problem">
              <p className="text-[15px] leading-relaxed" style={{ color: NAVY }}>{b.problem_statement}</p>
            </ResultSection>
          )}
          {Array.isArray(b.business_currency) && b.business_currency.length > 0 && (
            <ResultSection title="Your business currency" eyebrow="Section 2" accentGradient="linear-gradient(135deg,#FF6A00 0%,#CA8A04 100%)" group={GROUP} testid="public-brief-currency">
              <ReportList color={RK.amber} items={b.business_currency} />
            </ResultSection>
          )}
          {b.superpower_play && (
            <ResultSection title="Superpower play" eyebrow="Section 3" accentGradient="linear-gradient(135deg,#FBBF24 0%,#FF6A00 100%)" group={GROUP} testid="public-brief-superpower">
              <p className="text-[15px] leading-relaxed" style={{ color: NAVY }}>{b.superpower_play}</p>
            </ResultSection>
          )}
          {Array.isArray(b.network_moves) && b.network_moves.length > 0 && (
            <ResultSection title="Network moves" eyebrow="Section 4" accentGradient="linear-gradient(135deg,#0E5A4A 0%,#064E3B 100%)" group={GROUP} testid="public-brief-network">
              <ReportList color={RK.green} items={b.network_moves} />
            </ResultSection>
          )}
          {b.company_intel && (
            <ResultSection title="Company intel" eyebrow="Section 5" accentGradient="linear-gradient(135deg,#3B82F6 0%,#1E40AF 100%)" group={GROUP} testid="public-brief-intel">
              <p className="text-[15px] leading-relaxed whitespace-pre-line" style={{ color: NAVY }}>{b.company_intel}</p>
            </ResultSection>
          )}
          {Array.isArray(b.ats_gaps) && b.ats_gaps.length > 0 && (
            <ResultSection title="ATS gaps to close" eyebrow="Section 6" accentGradient="linear-gradient(135deg,#991B1B 0%,#7C2D12 100%)" group={GROUP} testid="public-brief-gaps">
              <ReportList color={RK.red} items={b.ats_gaps} />
            </ResultSection>
          )}
          {Array.isArray(b.adjacent_roles) && b.adjacent_roles.length > 0 && (
            <ResultSection title="Adjacent roles to keep on radar" eyebrow="Section 7" accentGradient="linear-gradient(135deg,#A78BFA 0%,#6D28D9 100%)" group={GROUP} testid="public-brief-adjacent">
              <ReportList color={RK.purple} items={b.adjacent_roles} />
            </ResultSection>
          )}
        </div>

        <div className="rm-card-static p-5 mt-6 text-center"
             style={{ background: NAVY, borderColor: NAVY, boxShadow: `4px 4px 0 0 ${ORANGE}` }}>
          <div className="font-mono-rm uppercase text-[10px] tracking-widest mb-1"
               style={{ color: ORANGE, fontWeight: 700 }}>
            · Built with Resmo
          </div>
          <p className="text-sm mb-3" style={{ color: "#CBD5E1" }}>
            Resmo turns a resume + job description into a coach's briefing in under a minute.
          </p>
          <Link to="/" className="rm-btn text-sm px-4 py-2"
                style={{ background: ORANGE, color: "white", border: "2px solid white", fontWeight: 700 }}>
            Try Resmo free <Sparkles size={14} />
          </Link>
        </div>

        <footer className="text-center mt-6">
          <span className="font-mono-rm text-[10px] uppercase tracking-widest" style={{ color: "#94A3B8" }}>
            · {new Date(data.created_at).toLocaleDateString()} · Brief shared via Resmo
          </span>
        </footer>
      </div>
    </div>
  );
}
